import { EquipmentReference, CharacterData } from '../types/character';

// Equipment data structure from Content Library
// Now includes loot and consumables
export interface ResolvedEquipmentItem {
  id: string;
  name: string;
  type: 'weapon' | 'armor' | 'loot' | 'consumable';
  category: string;
  description?: string;
  feature?: string;
  modifiers?: any[];
  // Equipment type specific properties
  [key: string]: any;
}

// Resolved equipment with character-specific data
export interface ResolvedEquipmentWithCharacterData extends ResolvedEquipmentItem {
  quantity: number;
  equippedCount: number;
  // Computed properties
  isEquipped: boolean;
  unequippedCount: number;
}

// Cache for resolved equipment data
const equipmentCache = new Map<string, ResolvedEquipmentItem | null>();
const cacheTimestamps = new Map<string, number>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Resolves a single equipment reference to its Content Library data
 */
export const resolveEquipmentReference = async (itemId: string): Promise<ResolvedEquipmentItem | null> => {
  // Check cache first
  const cached = getCachedEquipment(itemId);
  if (cached !== undefined) {
    return cached;
  }

  try {
    // Import the equipment loading function
    const { findEquipment } = await import('./contentLibraryHelpers');
    const equipmentData = await findEquipment(itemId);
    
    if (!equipmentData) {
      // Cache null result to avoid repeated failed lookups
      setCachedEquipment(itemId, null);
      return null;
    }

    // Transform to resolved format
    const resolved: ResolvedEquipmentItem = {
      ...equipmentData,
      id: itemId,
      type: determineEquipmentType(equipmentData),
      modifiers: enhanceEquipmentModifiers(equipmentData, itemId)
    };

    // Cache the result
    setCachedEquipment(itemId, resolved);
    return resolved;
  } catch (error) {
    console.error(`Failed to resolve equipment reference: ${itemId}`, error);
    setCachedEquipment(itemId, null);
    return null;
  }
};

/**
 * Resolves multiple equipment references in batch
 */
export const resolveEquipmentReferences = async (
  references: EquipmentReference[]
): Promise<(ResolvedEquipmentWithCharacterData | null)[]> => {
  const promises = references.map(async (ref) => {
    const resolved = await resolveEquipmentReference(ref.itemId);
    if (!resolved) {
      return null;
    }

    return {
      ...resolved,
      quantity: ref.quantity,
      equippedCount: ref.equippedCount,
      isEquipped: ref.equippedCount > 0,
      unequippedCount: ref.quantity - ref.equippedCount
    };
  });

  return Promise.all(promises);
};

/**
 * Gets all equipped items for a character (for stat computation)
 */
export const getEquippedItems = async (character: CharacterData): Promise<ResolvedEquipmentItem[]> => {
  const equippedReferences = character.inventory.equipmentReferences.filter(ref => ref.equippedCount > 0);
  const resolvedItems = await resolveEquipmentReferences(equippedReferences);
  
  const equippedItems: ResolvedEquipmentItem[] = [];
  
  for (const item of resolvedItems) {
    if (item) {
      // Add multiple copies if multiple are equipped
      for (let i = 0; i < item.equippedCount; i++) {
        equippedItems.push({
          ...item,
          // Create unique IDs for each equipped instance
          id: `${item.id}_equipped_${i}`
        });
      }
    }
  }
  
  return equippedItems;
};

/**
 * Gets all inventory display items for UI (resolved with character data)
 */
export const getInventoryDisplayItems = async (character: CharacterData): Promise<ResolvedEquipmentWithCharacterData[]> => {
  const resolved = await resolveEquipmentReferences(character.inventory.equipmentReferences);
  return resolved.filter((item): item is ResolvedEquipmentWithCharacterData => item !== null);
};

/**
 * Enhances equipment modifiers by automatically creating modifiers for base properties
 */
const enhanceEquipmentModifiers = (equipmentData: any, itemId: string): any[] => {
  const modifiers = [...(equipmentData.modifiers || [])];
  
  // Add armor modifier if equipment has baseArmorScore
  if (equipmentData.baseArmorScore && equipmentData.baseArmorScore > 0) {
    const armorModifier = {
      id: `${itemId}_armor_auto`,
      source: {
        type: 'equipment',
        id: itemId,
        name: equipmentData.name || itemId
      },
      target: 'armor',
      operation: 'add',
      value: equipmentData.baseArmorScore,
      conditions: [],
      stackable: false,
      priority: 50
    };
    modifiers.push(armorModifier);
  }
  
  // Add threshold modifiers if equipment has baseThresholds
  if (equipmentData.baseThresholds) {
    if (equipmentData.baseThresholds.major) {
      const majorThresholdModifier = {
        id: `${itemId}_threshold_major_auto`,
        source: {
          type: 'equipment',
          id: itemId,
          name: equipmentData.name || itemId
        },
        target: 'thresholds.major',
        operation: 'set',
        value: equipmentData.baseThresholds.major,
        conditions: [],
        stackable: false,
        priority: 50
      };
      modifiers.push(majorThresholdModifier);
    }
    
    if (equipmentData.baseThresholds.severe) {
      const severeThresholdModifier = {
        id: `${itemId}_threshold_severe_auto`,
        source: {
          type: 'equipment',
          id: itemId,
          name: equipmentData.name || itemId
        },
        target: 'thresholds.severe',
        operation: 'set',
        value: equipmentData.baseThresholds.severe,
        conditions: [],
        stackable: false,
        priority: 50
      };
      modifiers.push(severeThresholdModifier);
    }
  }
  
  return modifiers;
};

/**
 * Determines equipment type from equipment data
 * Handles weapons, armor, loot, and consumables
 */
const determineEquipmentType = (equipmentData: any): 'weapon' | 'armor' | 'loot' | 'consumable' => {
  // Check explicit type first
  if (equipmentData.type === 'loot') {
    return 'loot';
  }
  if (equipmentData.type === 'consumable') {
    return 'consumable';
  }
  if (equipmentData.type === 'weapon' || equipmentData.category === 'weapon') {
    return 'weapon';
  }
  if (equipmentData.type === 'armor' || equipmentData.category === 'armor') {
    return 'armor';
  }
  // Default to loot for unknown items
  return 'loot';
};

/**
 * Cache management functions
 */
const getCachedEquipment = (itemId: string): ResolvedEquipmentItem | null | undefined => {
  const timestamp = cacheTimestamps.get(itemId);
  if (!timestamp || Date.now() - timestamp > CACHE_DURATION) {
    // Cache expired
    equipmentCache.delete(itemId);
    cacheTimestamps.delete(itemId);
    return undefined;
  }
  return equipmentCache.get(itemId);
};

const setCachedEquipment = (itemId: string, equipment: ResolvedEquipmentItem | null): void => {
  equipmentCache.set(itemId, equipment);
  cacheTimestamps.set(itemId, Date.now());
};

/**
 * Clears the equipment cache (useful for testing or manual refresh)
 */
export const clearEquipmentCache = (): void => {
  equipmentCache.clear();
  cacheTimestamps.clear();
};

/**
 * Gets cache statistics for debugging
 */
export const getEquipmentCacheStats = (): { size: number; items: string[] } => {
  return {
    size: equipmentCache.size,
    items: Array.from(equipmentCache.keys())
  };
};